'use client'

import React, { memo } from 'react'
import { Button } from '../ui/button'
import ToggleSwitch from '../ui/toggle-switch'
import SelectField from '../ui/select-field'
import { Slider } from '../ui/slider'
import { SettingsState, PerformanceMetrics } from '@/types/settings'

interface AudioSettingsPanelProps {
  settings: SettingsState
  performanceMetrics: PerformanceMetrics
  onSettingChange: (key: keyof SettingsState, value: any) => void
  onResetSection: (section: string) => void
}

const AudioSettingsPanel = memo<AudioSettingsPanelProps>(({
  settings,
  performanceMetrics,
  onSettingChange,
  onResetSection
}) => {
  return (
    <div className="space-y-6">
      {/* PERFORMANCE METRICS BAR */}
      <div className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg border border-slate-600/30">
        <div className="flex items-center space-x-4 text-xs">
          <span className="text-slate-400">Render:</span>
          <span className="text-cyan-400 font-mono">{performanceMetrics.renderTime}ms</span>
          <span className="text-slate-400">Memory:</span>
          <span className="text-green-400 font-mono">{performanceMetrics.memoryUsage}MB</span>
          <span className="text-slate-400">FPS:</span>
          <span className="text-purple-400 font-mono">{performanceMetrics.fps}</span>
        </div>
        <div className="flex space-x-2">
          <Button
            onClick={() => onResetSection('audio')}
            variant="outline"
            size="sm"
            className="h-6 px-2 text-xs"
          >
            Reset
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Sample Rate - ULTRA PRECISION */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-200" htmlFor="sampleRate">
            Sample Rate <span className="text-cyan-400">{settings.sampleRate}Hz</span>
          </label>
          <select
            id="sampleRate"
            value={settings.sampleRate}
            onChange={(e) => onSettingChange('sampleRate', parseInt(e.target.value))}
            className="w-full px-3 py-2 bg-slate-800/50 border border-slate-600/50 rounded-lg text-slate-200 focus:border-cyan-400/50 focus:outline-none focus:ring-1 focus:ring-cyan-400/30"
            aria-label="Select audio sample rate"
          >
            <option value={22050}>22050 Hz (Low Quality)</option>
            <option value={44100}>44100 Hz (CD Quality)</option>
            <option value={48000}>48000 Hz (DVD Quality)</option>
            <option value={96000}>96000 Hz (HD Audio)</option>
            <option value={192000}>192000 Hz (Ultra HD)</option>
          </select>
          <p className="text-xs text-slate-400">Higher = better quality, more CPU</p>
        </div>

        {/* Buffer Size - LATENCY CONTROL */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-200" htmlFor="bufferSize">
            Buffer Size <span className="text-green-400">{settings.bufferSize}smp</span>
          </label>
          <select
            id="bufferSize"
            value={settings.bufferSize}
            onChange={(e) => onSettingChange('bufferSize', parseInt(e.target.value))}
            className="w-full px-3 py-2 bg-slate-800/50 border border-slate-600/50 rounded-lg text-slate-200 focus:border-cyan-400/50 focus:outline-none focus:ring-1 focus:ring-cyan-400/30"
            aria-label="Select audio buffer size"
          >
            <option value={64}>64 samples (Ultra Low Latency)</option>
            <option value={128}>128 samples (Gaming)</option>
            <option value={256}>256 samples (Balanced)</option>
            <option value={512}>512 samples (Studio)</option>
            <option value={1024}>1024 samples (Max Quality)</option>
          </select>
          <p className="text-xs text-slate-400">Lower = less latency, more CPU</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Audio Bit Depth */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-200" htmlFor="audioBitDepth">
            Bit Depth <span className="text-orange-400">{settings.audioBitDepth}bit</span>
          </label>
          <SelectField
            id="audioBitDepth"
            value={settings.audioBitDepth}
            onChange={(value) => onSettingChange('audioBitDepth', value)}
            options={[
              { value: 8, label: '8-bit (Retro)' },
              { value: 16, label: '16-bit (CD Standard)' },
              { value: 24, label: '24-bit (Professional)' },
              { value: 32, label: '32-bit (Maximum)' }
            ]}
            valueParser={(val) => parseInt(val)}
            ariaLabel="Select audio bit depth"
          />
        </div>

        {/* Audio Channels */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-200" htmlFor="audioChannels">
            Channels <span className="text-purple-400">{settings.audioChannels}ch</span>
          </label>
          <SelectField
            id="audioChannels"
            value={settings.audioChannels}
            onChange={(value) => onSettingChange('audioChannels', value)}
            options={[
              { value: 1, label: 'Mono (1 channel)' },
              { value: 2, label: 'Stereo (2 channels)' },
              { value: 4, label: 'Quad (4 channels)' },
              { value: 6, label: '5.1 Surround (6 channels)' },
              { value: 8, label: '7.1 Surround (8 channels)' }
            ]}
            valueParser={(val) => parseInt(val)}
            ariaLabel="Select audio channels"
          />
        </div>
      </div>

      {/* Output Device */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-200" htmlFor="outputDevice">
          Output Device <span className="text-blue-400">{settings.outputDevice}</span>
        </label>
        <SelectField
          id="outputDevice"
          value={settings.outputDevice}
          onChange={(value) => onSettingChange('outputDevice', value)}
          options={[
            { value: 'default', label: 'System Default' },
            { value: 'speakers', label: 'Speakers' },
            { value: 'headphones', label: 'Headphones' },
            { value: 'external', label: 'External DAC' }
          ]}
          ariaLabel="Select audio output device"
        />
      </div>

      {/* SYNTHESIS ALGORITHM - QUANTUM PROCESSING */}
      <div className="space-y-6 p-6 bg-slate-800/30 rounded-lg border border-slate-600/30">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-lg flex items-center justify-center">
            <span className="text-lg">⚛️</span>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Synthesis Algorithm</h3>
            <p className="text-xs text-slate-400">Quantum processing parameters</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <Slider
            label="Complexity"
            value={[settings.complexity]}
            onValueChange={(value) => onSettingChange('complexity', value[0])}
            min={0}
            max={100}
            step={1}
            showValue={true}
            variant="cosmic"
            valueFormatter={(val) => `${val}%`}
            description="Algorithm complexity level"
          />

          <Slider
            label="Evolution"
            value={[settings.evolution]}
            onValueChange={(value) => onSettingChange('evolution', value[0])}
            min={0}
            max={100}
            step={1}
            showValue={true}
            variant="cosmic"
            valueFormatter={(val) => `${val}%`}
            description="Evolution rate parameter"
          />

          <Slider
            label="Resonance"
            value={[settings.resonance]}
            onValueChange={(value) => onSettingChange('resonance', value[0])}
            min={0}
            max={100}
            step={1}
            showValue={true}
            variant="cosmic"
            valueFormatter={(val) => `${val}%`}
            description="Resonance amplification"
          />

          <Slider
            label="Intensity"
            value={[settings.intensity]}
            onValueChange={(value) => onSettingChange('intensity', value[0])}
            min={0}
            max={100}
            step={1}
            showValue={true}
            variant="cosmic"
            valueFormatter={(val) => `${val}%`}
            description="Processing intensity"
          />

          <Slider
            label="Neural Drive"
            value={[settings.neuralDrive]}
            onValueChange={(value) => onSettingChange('neuralDrive', value[0])}
            min={0}
            max={100}
            step={1}
            showValue={true}
            variant="cosmic"
            valueFormatter={(val) => `${val}%`}
            description="Neural network drive"
          />

          <Slider
            label="Diffusion"
            value={[settings.diffusion]}
            onValueChange={(value) => onSettingChange('diffusion', value[0])}
            min={0}
            max={100}
            step={1}
            showValue={true}
            variant="cosmic"
            valueFormatter={(val) => `${val}%`}
            description="Signal diffusion rate"
          />

          <Slider
            label="Uncertainty"
            value={[settings.uncertainty]}
            onValueChange={(value) => onSettingChange('uncertainty', value[0])}
            min={0}
            max={100}
            step={1}
            showValue={true}
            variant="cosmic"
            valueFormatter={(val) => `${val}%`}
            description="Quantum uncertainty factor"
          />
        </div>
      </div>

      {/* QUANTUM PARAMETERS - ADVANCED PROCESSING */}
      <div className="space-y-6 p-6 bg-slate-800/30 rounded-lg border border-slate-600/30">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
            <span className="text-lg">⚛️</span>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Quantum Parameters</h3>
            <p className="text-xs text-slate-400">Advanced quantum processing</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <Slider
            label="Entanglement"
            value={[settings.entanglement]}
            onValueChange={(value) => onSettingChange('entanglement', value[0])}
            min={0}
            max={100}
            step={1}
            showValue={true}
            variant="cosmic"
            valueFormatter={(val) => `${val}%`}
            description="Quantum entanglement strength"
          />

          <Slider
            label="Superposition"
            value={[settings.superposition]}
            onValueChange={(value) => onSettingChange('superposition', value[0])}
            min={0}
            max={100}
            step={1}
            showValue={true}
            variant="cosmic"
            valueFormatter={(val) => `${val}%`}
            description="Quantum superposition state"
          />

          <Slider
            label="Quantum Tunneling"
            value={[settings.quantumTunneling]}
            onValueChange={(value) => onSettingChange('quantumTunneling', value[0])}
            min={0}
            max={100}
            step={1}
            showValue={true}
            variant="cosmic"
            valueFormatter={(val) => `${val}%`}
            description="Quantum tunneling probability"
          />

          <Slider
            label="Wave Function"
            value={[settings.waveFunction]}
            onValueChange={(value) => onSettingChange('waveFunction', value[0])}
            min={0}
            max={100}
            step={1}
            showValue={true}
            variant="cosmic"
            valueFormatter={(val) => `${val}%`}
            description="Wave function collapse rate"
          />
        </div>
      </div>

      {/* POWER TOGGLES */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Spatial Audio Toggle */}
        <div className="flex items-center justify-between p-6 bg-slate-800/30 rounded-lg border border-slate-600/30">
          <div>
            <h4 className="text-sm font-medium text-slate-200">Spatial Audio</h4>
            <p className="text-xs text-slate-400">3D positioning</p>
          </div>
          <ToggleSwitch
            value={settings.enableSpatialAudio}
            onChange={(value) => onSettingChange('enableSpatialAudio', value)}
            activeColor="cyan"
            title="Toggle spatial audio"
            ariaLabel={(value) => value ? "Disable spatial audio" : "Enable spatial audio"}
          />
        </div>

        {/* Hardware Acceleration */}
        <div className="flex items-center justify-between p-6 bg-slate-800/30 rounded-lg border border-slate-600/30">
          <div>
            <h4 className="text-sm font-medium text-slate-200">HW Acceleration</h4>
            <p className="text-xs text-slate-400">GPU processing</p>
          </div>
          <ToggleSwitch
            value={settings.enableHardwareAccel}
            onChange={(value) => onSettingChange('enableHardwareAccel', value)}
            activeColor="green"
            title="Toggle hardware acceleration"
            ariaLabel={(value) => value ? "Disable hardware acceleration" : "Enable hardware acceleration"}
          />
        </div>
      </div>
    </div>
  )
})

AudioSettingsPanel.displayName = 'AudioSettingsPanel'

export default AudioSettingsPanel